import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface DataTableColumn {
  key: string;
  header: ReactNode;
  headerAccessory?: ReactNode;
  sortable?: boolean;
}

interface DataTableProps {
  columns: DataTableColumn[];
  caption?: string;
  children: ReactNode;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  scrollable?: boolean;
}

export function DataTable({ columns, caption, children, sortColumn, sortDirection, onSort, scrollable }: Readonly<DataTableProps>) {
  return (
    // max-h fits thead (~40px) + 10 body rows (~40px each); scrolls vertically when rows exceed 10
    <div className={cn('overflow-x-auto border border-cool-gray-2 [&_td]:max-w-[15rem] [&_td]:break-words', scrollable && 'overflow-y-auto max-h-[440px]')}>
      <table className="min-w-full border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className={cn('bg-[#f6f6f6]', scrollable && 'sticky top-0 z-10')}>
          <tr>
            {columns.map((col) => {
              const isActive = sortColumn === col.key;
              let ariaSortValue: 'ascending' | 'descending' | 'none' = 'none';
              if (isActive) {
                ariaSortValue = sortDirection === 'asc' ? 'ascending' : 'descending';
              }
              const headerContent = col.sortable ? (
                <button
                  type="button"
                  onClick={() => onSort?.(col.key)}
                  className={cn(
                    'inline-flex items-center gap-1 hover:text-dds-blue focus:outline-none',
                    isActive && 'text-dds-blue',
                  )}
                >
                  {col.header}
                  {isActive && sortDirection === 'asc' && <ChevronUp size={14} />}
                  {isActive && sortDirection === 'desc' && <ChevronDown size={14} />}
                  {!isActive && <ChevronsUpDown size={14} className="text-cool-gray-7" />}
                </button>
              ) : (
                col.header
              );
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={col.sortable ? ariaSortValue : undefined}
                  className="border-b border-cool-gray-2 px-3 py-2 text-left text-sm font-semibold text-black whitespace-nowrap"
                >
                  {col.headerAccessory ? (
                    <div className="flex items-center gap-1">
                      {headerContent}
                      {col.headerAccessory}
                    </div>
                  ) : (
                    headerContent
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
